import { useMutation } from "@apollo/client/react";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { useCallback, useState } from "react";
import { readFragment } from "gql.tada";
import { useStore } from "@/app/store";
import {
  MARK_CALCULATION_COMPLETE,
  RECALCULATE_CALCULATION,
  REOPEN_CALCULATION,
} from "../graphql/mutations";
import { CALCULATION_DETAIL_FRAGMENT } from "../graphql/queries";
import {
  notComputableRecalculate,
  notComputableResults,
} from "../calculationMessages.constants";
import useStaleVersionHandler from "./useStaleVersionHandler";

export type LifecycleInFlight = "complete" | "recalculate" | "reopen" | null;

const useCalculationLifecycle = (onConflict: () => void) => {
  const handleStale = useStaleVersionHandler(onConflict);
  const setCalculation = useStore((s) => s.setCalculation);
  const [inFlight, setInFlight] = useState<LifecycleInFlight>(null);
  const [completeError, setCompleteError] = useState<string | null>(null);
  const [recalculateError, setRecalculateError] = useState<string | null>(null);

  const [runComplete] = useMutation(MARK_CALCULATION_COMPLETE);
  const [runRecalculate] = useMutation(RECALCULATE_CALCULATION);
  const [runReopen] = useMutation(REOPEN_CALCULATION);

  const markComplete = useCallback(async () => {
    const { calculation } = useStore.getState();
    if (!calculation) return;
    const calledForId = calculation.id;
    setInFlight("complete");
    setCompleteError(null);
    const result = await runComplete({
      variables: { id: calculation.id, expectedVersion: calculation.version },
    });
    setInFlight(null);
    if (result.error) {
      if (handleStale(result.error)) return;
      setCompleteError(mapLifecycleError(result.error));
      return;
    }
    if (result.data?.markCalculationComplete) {
      setCalculation(
        readFragment(
          CALCULATION_DETAIL_FRAGMENT,
          result.data.markCalculationComplete,
        ),
        calledForId,
      );
    }
  }, [runComplete, handleStale, setCalculation]);

  const recalculate = useCallback(async () => {
    const { calculation } = useStore.getState();
    if (!calculation) return;
    const calledForId = calculation.id;
    setInFlight("recalculate");
    setRecalculateError(null);
    const result = await runRecalculate({
      variables: { id: calculation.id, expectedVersion: calculation.version },
    });
    setInFlight(null);
    if (result.error) {
      if (handleStale(result.error)) return;
      setRecalculateError(mapLifecycleError(result.error, "recalculate"));
      return;
    }
    if (result.data?.recalculateCalculation) {
      setCalculation(
        readFragment(
          CALCULATION_DETAIL_FRAGMENT,
          result.data.recalculateCalculation,
        ),
        calledForId,
      );
    }
  }, [runRecalculate, handleStale, setCalculation]);

  const reopen = useCallback(async () => {
    const { calculation } = useStore.getState();
    if (!calculation) return;
    const calledForId = calculation.id;
    setInFlight("reopen");
    const result = await runReopen({
      variables: { id: calculation.id, expectedVersion: calculation.version },
    });
    setInFlight(null);
    if (result.error) {
      if (handleStale(result.error)) return;
      return;
    }
    if (result.data?.reopenCalculation) {
      setCalculation(
        readFragment(
          CALCULATION_DETAIL_FRAGMENT,
          result.data.reopenCalculation,
        ),
        calledForId,
      );
    }
  }, [runReopen, handleStale, setCalculation]);

  return {
    markComplete,
    recalculate,
    reopen,
    inFlight,
    completeError,
    recalculateError,
  };
};

function mapLifecycleError(
  error: unknown,
  action: "complete" | "recalculate" = "complete",
): string {
  const code = CombinedGraphQLErrors.is(error)
    ? error.errors[0]?.extensions?.["code"]
    : undefined;
  switch (code) {
    case "OPDATA_NOT_POPULATED":
      return "Operational data for this line/year is incomplete.";
    case "FORMULA_GRAMMAR_INVALID":
    case "FORMULA_UNKNOWN_VARIABLE":
      return "A cost formula is invalid or references a missing variable.";
    case "ILLEGAL_CALCULATION_STATE":
      return "This action isn't available in the current state — reload.";
    case "CALCULATION_NOT_COMPUTABLE":
      return action === "recalculate"
        ? notComputableRecalculate
        : notComputableResults;
    // CALCULATION_INCOMPLETE_RESULTS is the base type still thrown by legacy paths — same message.
    case "CALCULATION_MISSING_INPUT":
    case "CALCULATION_INCOMPLETE_RESULTS":
      return action === "recalculate"
        ? "Recalculation left some cost cells empty — check the latest operational data and inputs."
        : "Every cost cell must have a value before completing — some results are still empty.";
    default:
      return "This calculation could not be computed.";
  }
}

export default useCalculationLifecycle;
